/** Shared www / business site ids (Studio workspace + Nuxt `siteId`). */
export const SITE_IDS = {
  WWW: 'www',
  BUSINESS: 'business',
} as const;

export type SiteId = (typeof SITE_IDS)[keyof typeof SITE_IDS];

/** @deprecated Prefer {@link SiteId} */
export type FrontendSiteId = SiteId;

export const AUDIENCE_VALUES = [...Object.values(SITE_IDS), 'common'] as const;

export type AudienceValue = (typeof AUDIENCE_VALUES)[number];

export const AUDIENCE_OPTIONS: ReadonlyArray<{ title: string; value: AudienceValue }> = [
  { title: 'WWW', value: SITE_IDS.WWW },
  { title: 'Business', value: SITE_IDS.BUSINESS },
  { title: 'Common', value: 'common' },
];

/** Stable document IDs for the three audience taxonomy docs. Do not change once seeded. */
export const AUDIENCE_DOCUMENT_IDS = {
  [SITE_IDS.WWW]: 'audience-www',
  [SITE_IDS.BUSINESS]: 'audience-business',
  common: 'audience-common',
} as const satisfies Record<AudienceValue, string>;

export const AUDIENCE_DOCUMENT_ID_LIST = Object.values(AUDIENCE_DOCUMENT_IDS);

const SITE_PATH_PATTERN = new RegExp(
  `^/(${Object.values(SITE_IDS).join('|')})(?:/|$)`,
);

/**
 * Infer active Studio site from URL (`/{www|business}/…`).
 * Returns undefined when not in the browser or the path has no site segment.
 */
export function getStudioWorkspaceSiteId(pathname?: string): SiteId | undefined {
  if (pathname === undefined && typeof window === 'undefined') {
    return undefined;
  }

  const path = pathname ?? window.location.pathname;
  const match = path.match(SITE_PATH_PATTERN);
  const siteId = match?.[1];

  if (siteId === SITE_IDS.BUSINESS || siteId === SITE_IDS.WWW) {
    return siteId;
  }

  return undefined;
}

export function audienceDocumentId(value: AudienceValue): string {
  return AUDIENCE_DOCUMENT_IDS[value];
}

/**
 * Theme singleton ids encode audience (separate docs per workspace):
 * - `singleton-theme-{locale}` → www
 * - `singleton-theme-{locale}-business` → business
 *
 * Prefer this over a stored `audience` ref — editors must not retag www Themes as business.
 */
export function audienceFromThemeDocumentId(
  documentId: string | undefined | null,
): Exclude<AudienceValue, 'common'> | undefined {
  if (!documentId) {
    return undefined;
  }

  const publishedId = documentId
    .replace(/^drafts\./, '')
    .replace(/^versions\.[^.]+\./, '');

  if (!publishedId.startsWith('singleton-theme-')) {
    return undefined;
  }

  if (publishedId.endsWith('-business')) {
    return SITE_IDS.BUSINESS;
  }

  return SITE_IDS.WWW;
}

export function isAudienceDocumentId(id: string): boolean {
  const publishedId = id.replace(/^drafts\./, '');
  return (AUDIENCE_DOCUMENT_ID_LIST as readonly string[]).includes(publishedId);
}

/** Sanity reference value for an audience. */
export function audienceReference(value: AudienceValue) {
  return {
    _type: 'reference' as const,
    _ref: audienceDocumentId(value),
  };
}

/**
 * GROQ predicate for `apps/business`.
 * Business or common only — never www / untagged.
 */
export const BUSINESS_AUDIENCE_FILTER =
  `audience._ref in ["${AUDIENCE_DOCUMENT_IDS[SITE_IDS.BUSINESS]}", "${AUDIENCE_DOCUMENT_IDS.common}"]`;

/**
 * GROQ predicate: hide business-tagged documents.
 * Used by www frontend + www Studio so business content stays off the consumer site.
 */
export const EXCLUDE_BUSINESS_AUDIENCE_FILTER =
  `(!defined(audience._ref) || audience._ref != "${AUDIENCE_DOCUMENT_IDS[SITE_IDS.BUSINESS]}")`;

/**
 * GROQ predicate for documents visible on a frontend site.
 *
 * - **www**: exclude business-tagged docs (untagged / www / common OK)
 * - **business**: {@link BUSINESS_AUDIENCE_FILTER}
 */
export function audienceVisibilityFilter(siteId: SiteId): string {
  if (siteId === SITE_IDS.BUSINESS) {
    return BUSINESS_AUDIENCE_FILTER;
  }

  return EXCLUDE_BUSINESS_AUDIENCE_FILTER;
}

/** Prebuilt clause for `apps/web` — always excludes business-tagged docs. */
export const WWW_AUDIENCE_FILTER = EXCLUDE_BUSINESS_AUDIENCE_FILTER;

/**
 * GROQ predicate for Studio desk lists scoped to a workspace.
 * - www: www + untagged + common (never business-only)
 * - business: business + common
 *
 * Aligns with frontend visibility: shared (`common`) docs appear in both desks.
 */
export function studioWorkspaceAudienceFilter(workspace: SiteId): string {
  if (workspace === SITE_IDS.BUSINESS) {
    return `audience._ref in ["${AUDIENCE_DOCUMENT_IDS[SITE_IDS.BUSINESS]}", "${AUDIENCE_DOCUMENT_IDS.common}"]`;
  }

  return `(!defined(audience._ref) || audience._ref in ["${AUDIENCE_DOCUMENT_IDS[SITE_IDS.WWW]}", "${AUDIENCE_DOCUMENT_IDS.common}"])`;
}
